import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { SectionTitle } from "../../../components/primitives/data-display/SectionTitle";
import { Section } from "../../../components/primitives/layout/Section";
import {
  DataTable,
  type DataTableColumn,
} from "../../../components/primitives/data-display/DataTable";
import { gaps } from "../../../components/primitives/spacing";
import { Note } from "../../../components/primitives/data-display/Note";
import { AdmissionTracePanelBody } from "../../../components/domain/annotation/FieldReviewPanel/AdmissionTracePanel";
import { InvestigationsHeader } from "../InvestigationsHeader";
import { useUrlFilters } from "../../../lib/url_filters";
import { DOCUMENT_TRACE_TABS, documentTraceFiltersSchema } from "./document_trace_filters";
import { useDocumentExtractionTrace } from "./useDocumentExtractionTrace";
import type React from "react";
import type { AdmissionCheckResponse } from "../../../lib/corpusApi";

interface LookupFormProps {
  title: string;
  description: string;
  fieldLabel: string;
  initialValue: string;
  onSubmit: (value: string) => void;
}

/** Mount it fresh (`key`) when the document in the URL changes, so the field follows it. */
function LookupForm({ title, description, fieldLabel, initialValue, onSubmit }: LookupFormProps) {
  const [value, setValue] = useState(initialValue);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  }

  return (
    <Stack gap={gaps.tight} sx={{ maxWidth: 700 }}>
      <SectionTitle>{title}</SectionTitle>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <Stack
        component="form"
        direction="row"
        gap={gaps.snug}
        onSubmit={handleSubmit}
        sx={{ mt: gaps.snug }}
      >
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          size="small"
          fullWidth
          inputProps={{ spellCheck: false, style: { fontFamily: "monospace" } }}
        />
        <Button type="submit" variant="contained" disabled={!value.trim()}>
          Inspect
        </Button>
      </Stack>
    </Stack>
  );
}
import type {
  DocumentConnectorAttempt,
  DocumentSectionClassification,
} from "../../../lib/corpusApi";
import { documentTracePath } from "../../../route_paths";

const OUTCOME_COLOR: Record<string, "success" | "warning" | "error" | "default"> = {
  success: "success",
  not_found: "warning",
  error: "error",
};

/** DataTable.getRowId only receives the row, not its index — these two response arrays have no
 *  field that's unique on its own (a section `key` like "funding" or "other" routinely repeats
 *  across multiple sections), so each row is decorated with its array index for a stable,
 *  collision-free row key. */
type Indexed<T> = T & { _rowIndex: number };

function indexed<T>(rows: T[]): Indexed<T>[] {
  return rows.map((row, _rowIndex) => ({ ...row, _rowIndex }));
}

const attemptColumns: DataTableColumn<Indexed<DocumentConnectorAttempt>>[] = [
  {
    key: "step",
    header: "Step",
    cell: (a) => <Typography sx={{ fontFamily: "monospace" }}>{a.step}</Typography>,
  },
  {
    key: "outcome",
    header: "Outcome",
    cell: (a) => (
      <Chip size="small" label={a.outcome} color={OUTCOME_COLOR[a.outcome] ?? "default"} />
    ),
  },
  {
    key: "detail",
    header: "Detail",
    cell: (a) => (
      <Typography variant="body2" color="text.secondary">
        {a.detail ?? "—"}
      </Typography>
    ),
  },
];

const sectionColumns: DataTableColumn<Indexed<DocumentSectionClassification>>[] = [
  {
    key: "key",
    header: "Section key",
    cell: (s) => <Typography sx={{ fontFamily: "monospace" }}>{s.key}</Typography>,
  },
  {
    key: "rawTitle",
    header: "Raw title",
    cell: (s) => (
      <Typography variant="body2" sx={{ fontStyle: s.rawTitle ? "normal" : "italic" }}>
        {s.rawTitle || "(none)"}
      </Typography>
    ),
  },
  { key: "method", header: "Classified via", cell: (s) => <Chip size="small" label={s.method} /> },
  {
    key: "confidence",
    header: "Confidence",
    cell: (s) => (s.confidence !== null ? s.confidence.toFixed(2) : "—"),
  },
];

const RECHECK_SEVERITY: Record<AdmissionCheckResponse["outcome"], "success" | "error" | "warning"> =
  {
    admitted: "success",
    rejected: "error",
    escalated: "warning",
  };

type DocumentTraceTab = (typeof DOCUMENT_TRACE_TABS)[number]["value"];

interface DocumentTraceProps {
  documentId: string;
}

/** What the pipeline did with one document: how its source was resolved, how its section titles
 *  were classified and how the corpus admission gate decided. Re-checking admission re-runs the
 *  gate against the document's current content and reloads the trace. */
function DocumentTrace({ documentId }: DocumentTraceProps) {
  const { data, notFound, isLoading, error, recheck } = useDocumentExtractionTrace(documentId);
  const [{ tab }, setFilters] = useUrlFilters(documentTraceFiltersSchema);

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Stack gap={gaps.base}>
      {notFound && (
        <Alert severity="warning">
          No extraction trace found for this document — it either predates trace storage, or the ID
          doesn't exist.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_event, value: DocumentTraceTab) => setFilters({ tab: value })}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {DOCUMENT_TRACE_TABS.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>

      {tab === "connectors" && (
        <DataTable
          columns={attemptColumns}
          rows={indexed(data?.connectorAttempts ?? [])}
          getRowId={(a) => String(a._rowIndex)}
          emptyMessage="No connector attempts recorded."
        />
      )}

      {tab === "sections" && (
        <DataTable
          columns={sectionColumns}
          rows={indexed(data?.sections ?? [])}
          getRowId={(s) => String(s._rowIndex)}
          emptyMessage="No sections recorded (unstructured or abstract-only document)."
        />
      )}

      {tab === "admission" && (
        <Section
          action={
            <Button size="small" onClick={() => void recheck.run()} disabled={recheck.loading}>
              {recheck.loading ? "Checking…" : "Re-check admission"}
            </Button>
          }
        >
          {recheck.error && <Alert severity="error">{recheck.error}</Alert>}
          {recheck.result && (
            <Alert severity={RECHECK_SEVERITY[recheck.result.outcome]}>
              {recheck.result.rejection?.message ??
                "Admitted — the document clears the corpus admission gate."}
            </Alert>
          )}
          {data?.admission ? (
            <AdmissionTracePanelBody admission={data.admission} />
          ) : (
            <Note>
              No admission data recorded — this document predates the admission gate or was
              escalated without a conclusive assessment.
            </Note>
          )}
        </Section>
      )}
    </Stack>
  );
}

/** Internal page — inspect what the pipeline did with a document: which connector steps
 *  succeeded/failed, how each section title was classified, and how the admission gate decided.
 *  Opens from a link on a document's details (`/:documentId`) or from a pasted ID. Debug and
 *  observability, secondary to the annotation workflow (see design-documents/024). */
export default function DocumentExtractionTrace() {
  const { documentId } = useParams();
  const navigate = useNavigate();

  return (
    <InternalPage width="full">
      <InvestigationsHeader />
      <LookupForm
        key={documentId ?? ""}
        title="Document trace"
        description="Paste a document (source) ID to inspect how its source was resolved, how each section title was classified and how the corpus admission gate decided."
        fieldLabel="Document ID"
        initialValue={documentId ?? ""}
        onSubmit={(id) => navigate(documentTracePath(id))}
      />
      {documentId && <DocumentTrace key={documentId} documentId={documentId} />}
    </InternalPage>
  );
}
